import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import useUtm from 'src/hooks/useUtm';
import { addUtmToInternalLink } from 'src/utils/utm';

declare global {
    interface Window {
        __UTICKET_DEBUG?: {
            clearAllUtm?: () => void;
            [key: string]: any;
        };
    }
}

/**
 * Componente global para rastrear parâmetros UTM em toda a aplicação.
 * 
 * Este componente:
 * 1. Captura UTMs da URL atual
 * 2. Adiciona UTMs a todos os links internos automaticamente durante a sessão ativa
 * 3. Preserva UTMs durante navegação pelo histórico na sessão ativa
 * 
 * Regra de negócio:
 * - A propagação de UTM só é ativada quando o usuário entra com UTM em páginas permitidas
 * - Os parâmetros UTM só são salvos na landing page (/solucoes) ou na página de criar evento (me/events/new/what)
 * - A propagação é encerrada depois que o first touch é registrado em uma página permitida
 * - Os parâmetros UTM só aparecem na URL quando o usuário está em uma página permitida
 */
const UTMTracker: React.FC = () => {
    const {
        getUtmForPropagation,
        shouldPropagateUTM,
        clearAllUtm,
        shouldShowUtmInUrl,
        getSessionUTM
    } = useUtm();
    const location = useLocation();

    useEffect(() => {
        const hostname = window.location.hostname;
        const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
        const isPrUticketDev = hostname === 'pr.uticket.dev.br' || hostname.endsWith('.pr.uticket.dev.br');
        const isQaUticketDev = hostname === 'uticket.dev.br' || (hostname.includes('uticket.dev.br') && !isPrUticketDev);
        const isAllowedEnvironment = isLocalhost || isPrUticketDev || isQaUticketDev;

        if (isAllowedEnvironment) {
            window.__UTICKET_DEBUG = window.__UTICKET_DEBUG || {};
            window.__UTICKET_DEBUG.clearAllUtm = () => {
                clearAllUtm();
                window.location.reload();
            };

            console.log(
                '%c[UTMTracker]',
                'color: #4a90e2; font-weight: bold',
                'Para limpar todos os dados UTM para testes, execute no console: window.__UTICKET_DEBUG.clearAllUtm()'
            );
        }

        return () => {
            if (window.__UTICKET_DEBUG) {
                delete window.__UTICKET_DEBUG.clearAllUtm;
            }
        };
    }, [clearAllUtm]);

    useEffect(() => {
        const handleLinkClick = (event: MouseEvent) => {
            if (!shouldPropagateUTM()) {
                return;
            }

            const target = event.target as HTMLElement;
            const link = target.closest('a');

            if (!link || !link.href || link.getAttribute('data-no-utm') === 'true') {
                return;
            }

            const isExternalLink = link.target === '_blank' ||
                !link.href.startsWith(window.location.origin);

            try {
                const destUrl = new URL(link.href, window.location.href);
                const destPath = destUrl.pathname || '';
                const isReferralDestination = destPath.startsWith('/indicacao');
                if (isReferralDestination) {
                    return; 
                }
            } catch (e) {
            }

            if (isExternalLink) {
                const permanentUtm = getUtmForPropagation();
                const tempUtm = getSessionUTM();
                const utmString = permanentUtm || tempUtm;

                if (utmString && !link.href.includes('utm_')) {
                    event.preventDefault();

                    const newUrl = addUtmToInternalLink(link.href, utmString);

                    if (link.target === '_blank') {
                        window.open(newUrl, '_blank');
                    } else {

                        window.location.href = newUrl;
                    }
                }
            }
            else if (link.href.startsWith(window.location.origin)) {
                const utmString = getUtmForPropagation();

                if (utmString && !link.href.includes('utm_')) {
                    event.preventDefault();

                    const shouldShowUtm = shouldShowUtmInUrl();

                    if (shouldShowUtm) {
                        const newUrl = addUtmToInternalLink(link.href, utmString);
                        window.location.href = newUrl;
                    } else {
                        window.location.href = link.href;
                    }
                }
            }
        };

        document.addEventListener('click', handleLinkClick);

        return () => {
            document.removeEventListener('click', handleLinkClick);
        };
    }, [getUtmForPropagation, shouldPropagateUTM, shouldShowUtmInUrl, getSessionUTM]);

    useEffect(() => {
        const handlePopState = () => {
            if (!shouldPropagateUTM()) {
                return;
            }

            if (window.location.pathname.startsWith('/indicacao')) {
                return;
            }

            const utmString = getUtmForPropagation();
            const shouldShowUtm = shouldShowUtmInUrl();

            if (utmString && !window.location.search.includes('utm_') && shouldShowUtm) {
                const newUrl = addUtmToInternalLink(window.location.href, utmString);
                window.history.replaceState(null, '', newUrl);
            }
        };

        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [getUtmForPropagation, shouldPropagateUTM, shouldShowUtmInUrl]);

    useEffect(() => {
        if (!shouldPropagateUTM()) {
            return;
        }

        if (location.pathname.startsWith('/indicacao')) {
            return;
        }

        const utmString = getUtmForPropagation();
        const shouldShowUtm = shouldShowUtmInUrl();

        if (utmString && !window.location.search.includes('utm_') && shouldShowUtm) {
            const newUrl = addUtmToInternalLink(window.location.href, utmString);
            window.history.replaceState(null, '', newUrl);
        }
    }, [location.pathname, getUtmForPropagation, shouldPropagateUTM, shouldShowUtmInUrl]);

    return null;
};

export default UTMTracker;
